import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Star, Store, UtensilsCrossed } from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import { useCategorias, useComercios, type ComercioPublico } from '@/lib/queries';
import { puntoDeBusqueda, useUbicacion } from '@/lib/ubicacion';
import { Cargando, ErrorCarga, Vacio } from '@/components/ui';
import { CarruselAnuncios } from '@/components/CarruselAnuncios';

type Orden = 'distance' | 'rating' | 'popular';

export function Inicio() {
  const ubicacion = useUbicacion((s) => s.ubicacion);
  const punto = puntoDeBusqueda(ubicacion);

  const [categoria, setCategoria] = useState<string | undefined>();
  const [orden, setOrden] = useState<Orden>('distance');

  const categorias = useCategorias();
  const comercios = useComercios({
    lat: punto.lat,
    lng: punto.lng,
    radius: 15_000,
    categoryId: categoria,
    sort: orden,
  });

  return (
    <div className="pb-20 sm:pb-0">
      {/* Lo primero que se ve: si no hay anuncios vivos, no ocupa nada. */}
      <CarruselAnuncios />

      {/* Categorías */}
      {categorias.data && categorias.data.length > 0 && (
        <nav aria-label="Categorías" className="-mx-4 mb-6 px-4 sm:mx-0 sm:px-0">
          <ul className="flex gap-2 overflow-x-auto pb-2">
            <li>
              <BotonCategoria
                activa={!categoria}
                onClick={() => setCategoria(undefined)}
                nombre="Todos"
              />
            </li>
            {categorias.data
              .filter((c) => c.merchantCount > 0)
              .map((c) => (
                <li key={c.id}>
                  <BotonCategoria
                    activa={categoria === c.id}
                    onClick={() => setCategoria(categoria === c.id ? undefined : c.id)}
                    nombre={c.name}
                    cantidad={c.merchantCount}
                  />
                </li>
              ))}
          </ul>
        </nav>
      )}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">
            {categoria
              ? categorias.data?.find((c) => c.id === categoria)?.name
              : 'Comercios cerca de ti'}
          </h1>
          <p className="mt-1 flex items-center gap-1 text-sm text-kick-gray">
            <MapPin className="h-3.5 w-3.5" />
            {ubicacion?.label ?? 'Medellín, centro'}
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-kick-gray">Ordenar por</span>
          <select
            value={orden}
            onChange={(e) => setOrden(e.target.value as Orden)}
            className="rounded-kick border border-kick-gray/25 bg-white px-3 py-1.5 outline-none focus:border-kick-red"
          >
            <option value="distance">Cercanía</option>
            <option value="rating">Calificación</option>
            <option value="popular">Populares</option>
          </select>
        </label>
      </div>

      {comercios.isLoading && <Cargando texto="Buscando comercios…" />}

      {comercios.isError && (
        <ErrorCarga
          mensaje={getErrorMessage(comercios.error)}
          onReintentar={() => comercios.refetch()}
        />
      )}

      {comercios.data?.length === 0 && (
        <Vacio
          icono={<Store className="h-6 w-6" />}
          titulo="No hay comercios por aquí"
          descripcion={
            categoria
              ? 'Prueba con otra categoría o cambia tu dirección de entrega.'
              : 'Todavía no tenemos comercios que lleguen a esta dirección.'
          }
        />
      )}

      {comercios.data && comercios.data.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {comercios.data.map((comercio) => (
            <li key={comercio.id}>
              <TarjetaComercio comercio={comercio} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BotonCategoria({
  activa,
  onClick,
  nombre,
  cantidad,
}: {
  activa: boolean;
  onClick: () => void;
  nombre: string;
  cantidad?: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={activa}
      className={cn(
        'whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition',
        activa
          ? 'bg-kick-red text-white'
          : 'bg-white text-kick-ink shadow-card hover:bg-kick-light',
      )}
    >
      {nombre}
      {cantidad !== undefined && (
        <span className={cn('ml-1.5', activa ? 'text-white/70' : 'text-kick-gray')}>
          {cantidad}
        </span>
      )}
    </button>
  );
}

export function TarjetaComercio({ comercio }: { comercio: ComercioPublico }) {
  return (
    <Link
      to={`/c/${comercio.slug}`}
      className={cn(
        'card-kick block h-full overflow-hidden',
        !comercio.isOpen && 'opacity-75',
      )}
    >
      <div className="relative h-32 bg-kick-light">
        {comercio.coverUrl ? (
          <img
            src={comercio.coverUrl}
            alt=""
            loading="lazy"
            className={cn('h-full w-full object-cover', !comercio.isOpen && 'grayscale')}
          />
        ) : (
          <div className="grid h-full place-items-center text-kick-gray/30">
            <UtensilsCrossed className="h-8 w-8" />
          </div>
        )}

        {!comercio.isOpen && (
          <div className="absolute inset-0 grid place-items-center bg-black/50">
            <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-kick-dark">
              Cerrado
            </span>
          </div>
        )}

        {comercio.logoUrl && (
          <img
            src={comercio.logoUrl}
            alt=""
            loading="lazy"
            className="absolute -bottom-5 left-3 h-12 w-12 rounded-kick border-2 border-white object-cover shadow"
          />
        )}
      </div>

      <div className={cn('p-3', comercio.logoUrl && 'pt-7')}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="truncate font-semibold text-kick-ink">{comercio.name}</h2>
          {comercio.ratingCount > 0 && (
            <span className="flex shrink-0 items-center gap-0.5 text-sm">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              <span className="font-semibold text-kick-ink">
                {comercio.ratingAvg.toFixed(1)}
              </span>
            </span>
          )}
        </div>

        <p className="truncate text-sm text-kick-gray">{comercio.categoryName}</p>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-kick-gray">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {comercio.avgPrepMinutes} min
          </span>
          {comercio.distanceMeters !== undefined && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {formatDistance(comercio.distanceMeters)}
            </span>
          )}
          {comercio.minOrderAmount > 0 && (
            <span>Mínimo {formatCOP(comercio.minOrderAmount)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
